import type { MutableRefObject } from 'react'
import { useDebouncedCallback } from '@/scopes/common/components/hooks/useDebouncedCallback'
import { useAnalytics } from '@/scopes/common/infrastructure/analytics/hooks'

import { usePagination } from '@/scopes/search/search/domain/hooks'

import { mqMin } from '@sunwebgroup/ui-components'

import { Pagination } from '@sunwebgroup/ui-components/uds'
import * as React from 'react'
import { styled } from 'styled-components'

const SMALL_SCREEN_BREAKPOINT = '370px'
interface PaginationProps {
  scrollToElement: MutableRefObject<HTMLDivElement | null>
}

const SearchWrapper = styled.div`
  max-width: 100%;
  margin: auto;

  @media (min-width: 430px) {
    max-width: 375px;
  }

  @media ${mqMin('primitives_breakpoint_m_min')} {
    max-width: 480px;
  }
`

const PageButtonWrapper = styled(Pagination.PageButton)<{ selected: boolean }>`
  display: none;

  ${({ selected }) => selected && `
    display: initial;
  `}

  @media (min-width: ${SMALL_SCREEN_BREAKPOINT}) {
    display: initial;
  }
`

const EllipsisWrapper = styled(Pagination.Ellipsis)`
  display: none;

  @media (min-width: ${SMALL_SCREEN_BREAKPOINT}) {
    display: flex;
  }
`

export function SearchPagination({ scrollToElement }: PaginationProps) {
  const {
    isFetching,
    isFirstPage,
    isLastPage,
    currentPage,
    paginationRange,
    setNextPage,
    setPreviousPage,
    setPage,
    DOTS,
  } = usePagination()
  const { sendEvent } = useAnalytics()

  const scrollToTop = useDebouncedCallback(() => {
    requestAnimationFrame(() => {
      if (scrollToElement.current) {
        const rect = scrollToElement.current.getBoundingClientRect()

        scrollTo({
          top: rect.top + window.scrollY,
          behavior: 'smooth',
        })
      }
    })
  }, 500)

  const sendPaginationSearchEvent = (pageNumber: number) => {
    sendEvent({
      event: 'paginationSearch',
      description: 'pagination of product listing',
      element: 'number',
      type: 'result page',
      result: pageNumber,
    })
  }

  const handlePreviousPage = () => {
    sendPaginationSearchEvent(currentPage - 1)
    setPreviousPage()
    scrollToTop()
  }

  const handleNextPage = () => {
    sendPaginationSearchEvent(currentPage + 1)
    setNextPage()
    scrollToTop()
  }

  const handleSetPage = (pageNumber: number | string) => {
    setPage(pageNumber)
    scrollToTop()
  }

  return (
    <SearchWrapper>
      <Pagination.Root>
        <Pagination.PreviousPageButton
          onClick={handlePreviousPage}
          disabled={isFetching || isFirstPage}
        />
        {paginationRange.map((pageNumber: number | string, index: number) => {
          if (pageNumber === DOTS) {
            return (
              <EllipsisWrapper key={index} />
            )
          }

          return (
            <PageButtonWrapper
              onClick={() => handleSetPage(pageNumber)}
              selected={pageNumber === currentPage}
              key={index}
            >
              {pageNumber}
            </PageButtonWrapper>
          )
        })}
        <Pagination.NextPageButton
          onClick={handleNextPage}
          disabled={isFetching || isLastPage}
        />
      </Pagination.Root>
    </SearchWrapper>
  )
}
